// noinspection BadExpressionStatementJS

import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { MatSelectChange } from '@angular/material/select';
import {
  APIHTTPErrorResponse,
  ExistingUserRegistrationFormPayload,
} from '../../../core/models/API.models';
import { UserService } from '../../../core/services/user/user.service';
import { TokenService } from '../../../core/services/auth/token.service';
import {
  Country,
  CountryCode,
  createNameValidator,
  createOneRequiredCharacterValidator,
  excludedUSRegions,
  FormControlName,
  FormGroupName,
  JoinFlowStep,
  msawEmailDomainToExclude,
} from './join-form';
import { Subscription } from 'rxjs';
import { AppRoute } from 'src/app/routing/routing.config';
import { NGXLogger } from 'ngx-logger';
import {
  AppErrorCode,
  BackEndErrorState,
} from 'src/app/core/services/error/error.constants';
import { ErrorService } from 'src/app/core/services/error/error.service';
import { CA, US } from './jurisdictions';
import { AnalyticsService } from 'src/app/core/services/analytics/analytics.service';
import { RoutingUtils } from 'src/app/routing/routing.utils';

US[2] = US[2].filter(
  (reg) => !excludedUSRegions.map((r) => r.shortCode).includes(reg[1])
);

@Component({
  selector: 'app-join-form',
  templateUrl: './join-form.component.html',
  styleUrls: ['./join-form.component.scss'],
})
export class JoinFormComponent implements OnInit, OnDestroy {
  public steps: JoinFlowStep[] = [
    JoinFlowStep.STEP_1,
    JoinFlowStep.STEP_2,
    JoinFlowStep.STEP_3,
    JoinFlowStep.PROCESSING,
    JoinFlowStep.FINISH_REGISTRATION,
    JoinFlowStep.ERROR,
  ];
  public step = 0;

  errorState: BackEndErrorState;
  publicErrorCode: string;
  supportEmailHref: string;
  BackEndErrorState = BackEndErrorState;

  submitTouched = false;

  FormGroupName = FormGroupName;

  FormControlName = FormControlName;

  JoinFlowStep = JoinFlowStep;

  joinFlowForm = new FormGroup({
    [FormGroupName.firstStep]: new FormGroup({
      [FormControlName.firstName]: new FormControl('', [createNameValidator()]),
      [FormControlName.lastName]: new FormControl('', [createNameValidator()]),
      [FormControlName.email]: new FormControl('', [
        Validators.required,
        Validators.email,
      ]),

      // Based on enforecement rules provided by Paul
      // https://www.oreilly.com/library/view/regular-expressions-cookbook/9781449327453/ch04s19.html
      // https://stackoverflow.com/questions/19605150/regex-for-password-must-contain-at-least-eight-characters-at-least-one-number-a
      [FormControlName.password]: new FormControl('', [
        Validators.required,
        Validators.pattern(
          /^(?=.{8,64}$)(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&\*\-\_\.])[A-Za-z\d!@#$%^&\*\-\_\.].*/
        ),
      ]),
    }),
    [FormGroupName.secondStep]: new FormGroup({
      [FormControlName.city]: new FormControl('', [
        createOneRequiredCharacterValidator(),
      ]),
      [FormControlName.province]: new FormControl('', [Validators.required]),
      [FormControlName.country]: new FormControl('', [Validators.required]),
      [FormControlName.zipCode]: new FormControl('', [Validators.required]),
      [FormControlName.employer]: new FormControl('', [
        createOneRequiredCharacterValidator(),
      ]),
      [FormControlName.jobTitle]: new FormControl('', [
        createOneRequiredCharacterValidator(),
      ]),
    }),
    [FormGroupName.thirdStep]: new FormGroup({
      [FormControlName.termsAndConditions]: new FormControl(false, [
        Validators.requiredTrue,
      ]),
    }),
  });

  firstAccessor = this.joinFlowForm.controls[FormGroupName.firstStep];
  secondAccessor = this.joinFlowForm.controls[FormGroupName.secondStep];
  thirdAccessor = this.joinFlowForm.controls[FormGroupName.thirdStep];

  countries: Country[] = [
    { value: CountryCode.CA, viewValue: 'Canada' },
    { value: CountryCode.US, viewValue: 'USA' },
  ];

  jurisdictions = {
    [CountryCode.MERGED]: [
      { viewValue: 'Canada', value: CA[2] },
      { viewValue: 'USA', value: US[2] },
    ],
    [CountryCode.CA]: CA[2],
    [CountryCode.US]: US[2],
  };

  countryCode = CountryCode;
  countryChangeSub: Subscription;
  jurisdictionChangeSub: Subscription;

  constructor(
    private router: Router,
    private userService: UserService,
    private tokenService: TokenService,
    private cdr: ChangeDetectorRef,
    private logger: NGXLogger,
    private errorService: ErrorService,
    private analyticsService: AnalyticsService,
    private routingUtils: RoutingUtils,
  ) {}

  async ngOnInit(): Promise<void> {
    this.countryChangeSub = this.secondAccessor
      .get(FormControlName.country)
      .valueChanges.subscribe((selectedCountry) => {
        this.secondAccessor.controls[FormControlName.zipCode].setValue('');
        this.secondAccessor.controls[FormControlName.province].setValue('');

        // Canadian postal code regex
        // based on https://stackoverflow.com/questions/15774555/efficient-regex-for-canadian-postal-code-function
        // verified with https://en.wikipedia.org/wiki/Postal_codes_in_Canada
        // /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ ]?\d[ABCEGHJ-NPRSTV-Z]\d$/i
        // ------
        // US Postel code regex
        // based on https://stackoverflow.com/questions/15774555/efficient-regex-for-canadian-postal-code-function
        // ^\d{5}(-{0,1}\d{4})?$

        let zipRegexp;

        if (selectedCountry === CountryCode.CA) {
          zipRegexp =
            /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ ]?\d[ABCEGHJ-NPRSTV-Z]\d$/i;
        } else if (selectedCountry === CountryCode.US) {
          zipRegexp = /^\d{5}(-{0,1}\d{4})?$/;
        }

        const zipCodeControl = this.secondAccessor.get(FormControlName.zipCode);
        zipCodeControl.clearValidators();
        zipCodeControl.addValidators([
          Validators.required,
          Validators.pattern(zipRegexp),
        ]);
        zipCodeControl.updateValueAndValidity();
      });

    await this.loadTokenAndUpdateForm();
    // this.stubFormData();
  }

  ngOnDestroy(): void {
    this.countryChangeSub.unsubscribe();
    // this.jurisdictionChangeSub.unsubscribe();
  }

  private stubFormData(): void {
    this.stubFormControlsWithData(this.firstAccessor, {
      [FormControlName.firstName]: 'Test F Name',
      [FormControlName.lastName]: 'Test L Name',
      [FormControlName.email]: 'kostiantyn+2@meetami.ca',
      [FormControlName.password]: 'Test_123',
    });

    this.stubFormControlsWithData(this.secondAccessor, {
      [FormControlName.city]: 'Toronto',
      [FormControlName.province]: 'Ontario',
      [FormControlName.country]: 'US',
      [FormControlName.zipCode]: '12345',
      [FormControlName.employer]: 'Bank Test',
      [FormControlName.jobTitle]: 'Developer Test',
    });
  }

  private stubFormControlsWithData(
    accessor: FormGroup,
    data: Record<string, string>
  ): void {
    Object.entries(accessor.controls).forEach(([key, control]) => {
      control.setValue(data[key]);
    });
  }

  private async loadTokenAndUpdateForm(): Promise<void> {
    const payload = this.tokenService.idTokenPayload();
    if (payload) {
      this.logger.debug('email:', payload.email);
      this.logger.debug('custom:ms-regionid: ', payload.country);

      if (!payload.email.includes(msawEmailDomainToExclude)) {
        this.firstAccessor.controls[FormControlName.email].setValue(
          payload.email
        );
      }

      const countryCode = CountryCode[payload.country];
      if (countryCode) {
        this.secondAccessor.controls[FormControlName.country].setValue(
          countryCode
        );
      }
      this.cdr.detectChanges();
    } else {
      this.logger.warn(
        '[JoinFormComponent] ngOnInit(): failed to load id token payload: '
      );
    }
  }

  controlAccess(
    formGroup: FormGroup,
    controlName: FormControlName
  ): AbstractControl {
    return formGroup.controls[controlName];
  }

  showErrorTextTermsAndConditions(): boolean {
    return (
      this.thirdAccessor.controls[FormControlName.termsAndConditions].invalid &&
      !this.thirdAccessor.controls[FormControlName.termsAndConditions].pristine
    );
  }

  accessorTrim(accessor: FormGroup, excludedControls: string[] = []): void {
    Object.entries(accessor.controls)
      .filter(([key]) => {
        return !excludedControls.includes(key);
      })
      .forEach(([, control]) => {
        if (control.value) {
          control.setValue(control.value.trim());
        }
      });
    this.logger.debug('FormGroup: ', accessor.controls);
  }

  submitFirstStep(): void {
    this.logger.debug(
      `Step #${this.step}, form valid status: ${this.firstAccessor.valid}`
    );

    this.submitTouched = true;
    this.accessorTrim(this.firstAccessor);

    if (this.firstAccessor.valid) {
      this.step++;
      this.submitTouched = false;

      this.analyticsService.pushEvent('start_registration', {
        category: 'Button Click',
        action: 'Form Submit',
        element: 'New User RegForm',
      });
    }
  }

  submitSecondStep(): void {
    this.logger.debug(
      `Step #${this.step}, form valid status: ${this.secondAccessor.valid}`
    );

    this.logger.debug(
      'FormControlName.jurisdiction: ',
      this.secondAccessor.get(FormControlName.province)
    );

    this.submitTouched = true;
    this.accessorTrim(this.secondAccessor, [
      FormControlName.country,
      FormControlName.zipCode,
    ]);

    if (this.secondAccessor.valid) {
      this.step++;
      this.submitTouched = false;
    }
  }

  async submitThirdStep(): Promise<void> {
    this.logger.debug(
      `Step #${this.step}, form valid status: ${this.thirdAccessor.valid}`
    );

    this.submitTouched = true;

    if (this.thirdAccessor.valid) {
      this.step++;
      this.submitTouched = false;

      this.logger.debug('API CALL', JSON.stringify(this.joinFlowForm.value));
      const formPayload = {
        ...this.joinFlowForm.value.firstStep,
        ...this.joinFlowForm.value.secondStep,
        ...this.joinFlowForm.value.thirdStep,
      } as ExistingUserRegistrationFormPayload;

      this.analyticsService.pushEvent('submit_registration', {
        category: 'Button Click',
        action: 'Form Submit',
        element: 'New User RegForm Submit',
      });

      try {
        const isRegistered = await this.userService.registerExistingUser(
          formPayload,
          this.joinFlowForm
        );

        if (isRegistered) {
          this.step++;
        } else {
          this.step--;
        }
      } catch (error) {
        this.renderErrorState(error);
      }
    }
  }

  goForward(): void {
    if (this.step < this.steps.length - 1) {
      this.step++;
    }
  }

  goBack(): void {
    if (this.step > 0) {
      this.step--;
    }
  }

  afterRegisteringClick(): void {
    this.router.navigate([AppRoute.REGISTERED_USER]);
  }

  async onErrorCTA(shouldResetFields = false): Promise<void> {
    this.logger.debug(
      'onErrorCTA called with this.errorState: ',
      this.errorState
    );

    if (BackEndErrorState.FIELDS_VALIDATION_FAILED == this.errorState) {
      this.setFormToInitialStep();
    } else if (BackEndErrorState.USER_ALREADY_REGISTERED == this.errorState) {
      const url = this.errorService.backEndErrorSuggestedActionUrl(
        this.errorState
      );
      this.routingUtils.routeToExternalUrl(url);
    }
  }

  onProvinceSelect(
    event: MatSelectChange,
    countryValue: CountryCode | ''
  ): void {
    if (countryValue === '') {
      for (const item of [CA, US]) {
        const countryCode = item[1];
        const regions = item[2];

        if (regions.some((region) => region[1] === event.value)) {
          this.secondAccessor.controls[FormControlName.country].setValue(
            countryCode
          );
        }
      }
    }
  }

  /** ERROR HANDLING */

  renderErrorState(error: Error) {
    this.logger.debug('error.constructor.name: ', error.constructor.name);

    // APIHTTPErrorResponse
    if (error instanceof APIHTTPErrorResponse) {
      this.logger.debug('registerExistingUser: Error - ', error);
      this.logger.debug(
        '[onErrorCTA]: sharedNavigationUrl',
        this.errorService.sharedNavigationUrl
      );

      this.initErrorVarsFromError(error);
      this.step = this.steps.length - 1;
    } else if (error instanceof Error) {
      const appError = (error?.cause ||
        AppErrorCode.UNKNOWN_ERROR) as AppErrorCode;
      this.errorService.routeToAuthError(appError);
    } else {
      // handling other errors
      this.errorService.routeToAuthError(AppErrorCode.UNKNOWN_ERROR);
    }
  }

  initErrorVarsFromError(err: APIHTTPErrorResponse) {
    this.publicErrorCode = err.error.errorCode;
    this.errorState = this.errorService.backEndErrorStateFromAPIErrorCode(
      this.publicErrorCode
    );
    this.supportEmailHref = this.errorService.supportEmailHref(
      this.publicErrorCode
    );
  }

  resetErrorCodes(): void {
    this.errorState = null;
    this.publicErrorCode = null;
    this.supportEmailHref = null;
  }

  setFormToInitialStep(): void {
    this.step = 0;
    this.resetErrorCodes();
  }

  async resetForm(shouldPrefillFields = true): Promise<void> {
    this.joinFlowForm.reset();
    this.setFormToInitialStep();

    if (shouldPrefillFields) {
      await this.loadTokenAndUpdateForm();
    }
  }
}
